import type { Components } from '@/api/generated/api';
import { downloadFile } from '@/utils/download.utils';
import { openUrlInNewTab } from '@/utils/dom.utils';
import { getFileUrl } from '@/api/resources/file.api';
import { QueryClient, useQueryClient, type QueryKey } from '@tanstack/vue-query';
import { useWhoAmIStore } from '@/stores/useWhoAmIStore';
import { getCurrentRole } from '@/utils/role.utils';
import { computed } from 'vue';
import { nowMs } from 'common/utils/date.utils';
import { FILE_URL_EXPIRY_MARGIN_RATIO, FILE_URL_MAX_EXPIRY_MARGIN_MS } from '@/constants/file.constants';
import { FileScanError } from '@/utils/file.scan.error';
import { ApiError } from '@/api/api.error';
import { FetchError } from '@/api/fetch.error';
import { fileKeys } from './file.keys';

type TDownload = Components.Schemas.FileUrlDto;

export function useFileUrl() {
  const client = useQueryClient();
  const store = useWhoAmIStore();
  const scope = computed(() => [store.whoAmI?.id, store.whoAmI?.currentTenantId, getCurrentRole(store.whoAmI)?.id]);
  const resolve = (fileId: number, force = false): Promise<TDownload> =>
    resolveFileUrl({ client, scope: scope.value, fileId, force });
  const scanRetryInterval = (fileId: number): number | false => {
    const state = client.getQueryState<TDownload>(fileKeys.url(scope.value, fileId));
    if (state?.data?.malwareScanStatus !== 'pending') return false;
    if (state.status === 'error' && !isTransientFileRequestError(state.error)) return false;
    // Failed refetches retain the last response; use it only to schedule another check.
    const retryAfterSeconds = state.data.retryAfterSeconds;
    return retryAfterSeconds ? retryAfterSeconds * 1000 : false;
  };
  const open = async (fileId: number, newTab = false): Promise<void> => {
    // Resolve access and scan status before opening a tab or leaving the current page.
    const url = requireFileUrl(await resolve(fileId));
    if (newTab) openUrlInNewTab(url);
    else window.location.assign(url);
  };
  const download = async (fileId: number, filename: string): Promise<void> => {
    const url = requireFileUrl(await resolve(fileId));
    const response = await fetch(url);
    if (!response.ok) throw new Error(`File download failed (${response.status})`);
    downloadFile({ file: await response.blob(), fileName: filename });
  };
  return { resolve, open, download, scope, scanRetryInterval };
}

function isTransientFileRequestError(error: unknown): boolean {
  if (error instanceof FetchError) return true;
  if (!(error instanceof ApiError) || error.httpStatus == null) return false;
  return error.httpStatus === 408 || error.httpStatus === 429 || (error.httpStatus >= 500 && error.httpStatus < 600);
}

/** Return delivery status, including pending scans. Cache lifetime follows the URL and next scan recheck. */
export async function resolveFileUrl(params: {
  client: QueryClient;
  scope: QueryKey;
  fileId: number;
  force?: boolean;
}): Promise<TDownload> {
  const { client, scope, fileId, force } = params;
  const queryKey = fileKeys.url(scope, fileId);
  const cached = client.getQueryState<TDownload>(queryKey);
  if (!force && cached?.status === 'success' && cached.data && isFileUrlFresh(cached.data, cached.dataUpdatedAt)) {
    return cached.data;
  }
  return client.fetchQuery({
    queryKey,
    queryFn: () => getFileUrl(fileId),
    staleTime: 0,
    retry: false,
  });
}

function isFileUrlFresh(download: TDownload, dataUpdatedAt: number): boolean {
  const expiresAt = new Date(download.expiresAt).getTime();
  const remainingLifetime = Math.max(0, expiresAt - dataUpdatedAt);
  const margin = Math.min(FILE_URL_MAX_EXPIRY_MARGIN_MS, remainingLifetime * FILE_URL_EXPIRY_MARGIN_RATIO);
  const scanCheckAt = download.retryAfterSeconds ? dataUpdatedAt + download.retryAfterSeconds * 1000 : Infinity;
  return nowMs() < Math.min(expiresAt - margin, scanCheckAt);
}

/** Explicit open/download actions need a URL; otherwise their caller shows the restriction as a toast. */
export function requireFileUrl(download: TDownload): string {
  if (download.url) return download.url;
  throw new FileScanError(download.malwareScanStatus);
}
